/**
 * Выгрузка сканов участников в браузере администратора (задача 41,
 * docs/tasks/41-admin-documents-download.md).
 *
 * Архив собирает **браузер**, а не сервер: у HTTP-триггера функции ответ ограничен 3,5 МБ,
 * время — 15 с, а скан — до 10 МБ, поэтому собрать 1000 спортсменов × 3 документа в
 * облаке невозможно физически. Порядок работы:
 *
 * 1. манифест (`GET /api/admin/exports/documents`) — перечень команд и метаданных файлов;
 * 2. файлы забираются по одному: в облаке — подписанными ссылками прямо из Object Storage,
 *    в разработке и контейнере — base64 через бэкенд;
 * 3. содержимое складывается в архивы `<команда>.zip` (по одному на команду) и отдаётся
 *    браузеру системным скачиванием.
 *
 * Режим «папками» через `showDirectoryPicker` убран 23.10.2026: Chrome отклонял выбор
 * любого каталога («Не удалось открыть папку — она содержит системные файлы»).
 *
 * Почему **архив на команду**, а не один общий ZIP на 1,2 ГБ: готовый архив нельзя
 * дописать (без центрального каталога он битый), а по командам выгрузка возобновляемая —
 * повторный запуск докачивает недостающие команды и не перекачивает готовые. Плюс архив
 * команды (десятки мегабайт) влезает в память вкладки, а «всё сразу» — нет.
 *
 * Логика разделена: чистые функции (пути, имена, отпечаток, CSV) проверяются тестами без
 * сети и DOM, а сам прогон получает зависимости через `ExportIo` — поэтому отмена,
 * повторы и возобновляемость тоже тестируются без браузера.
 */
import { DOCUMENTS, documentTitle } from '../shared/dictionaries';
import type {
  DocumentsExportAthlete,
  DocumentsExportFile,
  DocumentsExportManifest,
  DocumentsExportTeam,
} from '../shared/types';
import type { DocumentRef } from './api';
import type { ExportSink } from './fileSink';
import { zipArchive, type ZipEntry } from './zipWriter';

/** Запрещённые в именах файлов Windows символы: выгрузку открывают и на Windows. */
const FORBIDDEN_IN_NAME = /[<>:"/\\|?*]/g;

/**
 * Убирает управляющие символы из имени: они ломают и файловую систему, и архивы.
 * Проверяются кодом символа, а не регулярным выражением: диапазон `\u0000-\u001f`
 * в шаблоне запрещает правило `no-control-regex` (eslint).
 */
function withoutControlCharacters(value: string): string {
  return Array.from(value)
    .filter((char) => char.charCodeAt(0) >= 0x20)
    .join('');
}

/** Предельная длина одного сегмента пути: длинное ФИО с инициалами режется, путь короче. */
const MAX_SEGMENT_LENGTH = 60;

/** Расширения по проверенному типу содержимого (белый список типов документов). */
const EXTENSIONS: Record<string, string> = {
  'application/pdf': 'pdf',
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/heic': 'heic',
};

/** Имя папки или файла: запрещённые символы заменяются пробелами, длина ограничена. */
export function safeSegment(value: string, fallback: string): string {
  const cleaned = withoutControlCharacters(value)
    .replace(FORBIDDEN_IN_NAME, ' ')
    .replace(/\s+/g, ' ')
    .replace(/[. ]+$/, '')
    .trim();
  const cut = cleaned.slice(0, MAX_SEGMENT_LENGTH).trim();
  return cut === '' ? fallback : cut;
}

/** Расширение файла: по проверенному MIME-типу, иначе — по имени, выбранному тренером. */
export function documentExtension(file: DocumentsExportFile): string {
  const byType = EXTENSIONS[file.contentType.toLowerCase()];
  if (byType) return byType;

  const dot = file.uploadedFileName.lastIndexOf('.');
  const byName =
    dot === -1
      ? ''
      : withoutControlCharacters(file.uploadedFileName.slice(dot + 1)).replace(
          FORBIDDEN_IN_NAME,
          '',
        );
  return byName === '' ? 'bin' : byName.slice(0, 8).toLowerCase();
}

/** Имя файла документа в папке спортсмена: название типа из словаря плюс расширение. */
export function documentFileName(file: DocumentsExportFile): string {
  return `${safeSegment(documentTitle(file.docId), file.docId)}.${documentExtension(file)}`;
}

/**
 * Имена папок спортсменов команды: однофамильцев (ФИО совпало символ в символ) различает
 * приписанный номер — иначе файлы разных людей слились бы в одной папке.
 */
export function athleteFolders(athletes: readonly DocumentsExportAthlete[]): Map<string, string> {
  const used = new Map<string, number>();
  const folders = new Map<string, string>();
  for (const athlete of athletes) {
    const base = safeSegment(athlete.fullName, athlete.athleteId.slice(0, 8));
    const seen = used.get(base) ?? 0;
    used.set(base, seen + 1);
    folders.set(athlete.athleteId, seen === 0 ? base : `${base} (${seen + 1})`);
  }
  return folders;
}

/** Короткий хеш строки (djb2): отпечаток выгрузки, а не криптография. */
function shortHash(value: string): string {
  let result = 5381;
  for (let index = 0; index < value.length; index += 1) {
    result = (result * 33 + value.charCodeAt(index)) >>> 0;
  }
  return result.toString(16);
}

/**
 * Отпечаток набора файлов команды: по нему повторная выгрузка понимает, что команда уже
 * скачана. Замена скана (другой размер или время загрузки) меняет отпечаток — команда
 * перекачивается, потому что на диске лежал бы устаревший файл.
 */
export function teamFingerprint(team: DocumentsExportTeam): string {
  const parts = team.athletes.flatMap((athlete) =>
    athlete.documents.map((file) => `${file.docId}:${file.size}:${file.uploadedAt}`),
  );
  // Части сортируются: перестановка строк в заявке не должна менять отпечаток —
  // иначе повторная выгрузка перекачивала бы команду без причины.
  return shortHash(parts.slice().sort().join('|'));
}

/** Файл в плане выгрузки: путь в папке/архиве и данные для запроса. */
export interface ExportFilePlan {
  ref: DocumentRef;
  /** Путь внутри архива: `<команда>/<спортсмен>/<документ>.<расширение>`. */
  path: string;
  /** Имя файла внутри папки спортсмена — оно же идёт в `_состав.csv`. */
  fileName: string;
  /** Размер по метаданным заявки: по нему считается прогресс выгрузки. */
  size: number;
}

/** Команда в плане выгрузки: папка, файлы и отпечаток для возобновления. */
export interface ExportTeamPlan {
  applicationId: string;
  organization: string;
  /** Имя папки команды: оно же имя архива `<команда>.zip`. */
  folder: string;
  files: ExportFilePlan[];
  fingerprint: string;
}

/** План выгрузки: что скачивать и в каком виде это ляжет на диск. */
export interface ExportPlan {
  teams: ExportTeamPlan[];
  files: number;
  bytes: number;
}

/**
 * Строит план выгрузки из манифеста: имена папок и файлов, пути и отпечатки команд.
 * Спортсмены без документов в план файлов не попадают, но остаются в `_состав.csv` —
 * организатору важно видеть, у кого сканов нет (комплектность проверяют на мероприятии).
 */
export function buildPlan(manifest: DocumentsExportManifest): ExportPlan {
  let files = 0;
  let bytes = 0;

  const teams = manifest.teams.map((team) => {
    const folder = safeSegment(team.organization, team.applicationId.slice(0, 8));
    const folders = athleteFolders(team.athletes);
    const planned = team.athletes.flatMap((athlete) =>
      athlete.documents.map((file) => {
        const fileName = documentFileName(file);
        return {
          ref: { ownerUserId: team.ownerUserId, athleteId: athlete.athleteId, docId: file.docId },
          path: `${folder}/${folders.get(athlete.athleteId) ?? athlete.athleteId}/${fileName}`,
          fileName,
          size: file.size,
        };
      }),
    );

    files += planned.length;
    bytes += planned.reduce((sum, item) => sum + item.size, 0);
    return {
      applicationId: team.applicationId,
      organization: team.organization,
      folder,
      files: planned,
      fingerprint: teamFingerprint(team),
    };
  });

  return { teams, files, bytes };
}

/** Разделитель CSV: точка с запятой — так русский Excel разбирает файл без настройки. */
const CSV_SEPARATOR = ';';

/** Поля строки состава — порядок колонок в `_состав.csv`. */
const COMPOSITION_COLUMNS = [
  'Команда',
  'Спортсмен',
  'Дата рождения',
  'Документ',
  'Файл',
  'Размер, байт',
  'Загружен',
  'Статус',
];

/** Значение CSV: значения с разделителем или переводом строки берутся в кавычки. */
function csvValue(value: string): string {
  return /[";\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

/** Строка CSV из значений. */
function csvRow(values: string[]): string {
  return values.map(csvValue).join(CSV_SEPARATOR);
}

/**
 * Строки состава команды: по строке на «спортсмен — тип документа». Отсутствующие
 * документы остаются строками со статусом «нет» — комплектность видно, не открывая папки.
 */
function compositionRows(planTeam: ExportTeamPlan, team: DocumentsExportTeam): string[] {
  const planned = new Map<string, ExportFilePlan>();
  for (const file of planTeam.files) {
    planned.set(`${file.ref.athleteId}|${file.ref.docId}`, file);
  }

  const rows: string[] = [];
  for (const athlete of team.athletes) {
    for (const type of DOCUMENTS) {
      const file = planned.get(`${athlete.athleteId}|${type.id}`);
      const uploaded = athlete.documents.find((item) => item.docId === type.id);
      rows.push(
        csvRow([
          planTeam.folder,
          athlete.fullName,
          athlete.birthDate,
          type.title,
          file?.fileName ?? '',
          uploaded ? String(uploaded.size) : '',
          uploaded?.uploadedAt ?? '',
          file ? 'есть' : 'нет',
        ]),
      );
    }
  }
  return rows;
}

/** CSV состава одной команды — лежит в её папке и внутри архива команды. */
export function teamCompositionCsv(planTeam: ExportTeamPlan, team: DocumentsExportTeam): string {
  return [csvRow(COMPOSITION_COLUMNS), ...compositionRows(planTeam, team)].join('\r\n');
}

/** CSV состава всех команд — один файл в корне выгрузки. */
export function manifestCompositionCsv(
  manifest: DocumentsExportManifest,
  plan: ExportPlan,
): string {
  const rows: string[] = [];
  for (const planTeam of plan.teams) {
    const team = manifest.teams.find((item) => item.applicationId === planTeam.applicationId);
    if (team) rows.push(...compositionRows(planTeam, team));
  }
  return [csvRow(COMPOSITION_COLUMNS), ...rows].join('\r\n');
}

/** Текст CSV в байтах: BOM в начале — иначе русский Excel открывает файл как cp1251. */
// Тип возврата указывает буфер явно: `Blob` принимает только вид на `ArrayBuffer`,
// а не на `SharedArrayBuffer` (иначе TS не пропускает `new Blob([bytes])`).
export function csvBytes(text: string): Uint8Array<ArrayBuffer> {
  return new TextEncoder().encode(`\uFEFF${text}`);
}

/** Размер в человекочитаемом виде: сообщение о выгрузке называет объём. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} Б`;
  const units = ['КБ', 'МБ', 'ГБ'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value < 10 ? 1 : 0)} ${units[unit]}`;
}

/** Одновременных скачиваний: быстрее последовательного обхода, но без перегрузки хранилища. */
const DOWNLOAD_CONCURRENCY = 4;

/** Попыток на один файл: сбой одиночного запроса не должен выпадать из выгрузки. */
const FILE_ATTEMPTS = 3;

/** Пауза перед повтором (мс); растёт вместе с номером попытки. */
const RETRY_PAUSE_MS = 500;

/** Пауза заданной длины. */
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Выполняет задачи пулом: одновременно не больше `limit` штук. Ошибки наружу не выходят —
 * каждая задача сама решает, что делать со своим сбоем (в выгрузке сбой файла попадает
 * в отчёт, а остальные файлы команды продолжают качаться).
 */
async function mapWithLimit<T>(
  items: readonly T[],
  limit: number,
  worker: (item: T) => Promise<void>,
): Promise<void> {
  let next = 0;
  const runners = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (next < items.length) {
      const item = items[next];
      next += 1;
      await worker(item);
    }
  });
  await Promise.all(runners);
}

/**
 * Память выгрузки: повторный запуск не перекачивает уже выгруженные команды.
 * Интерфейс отделён от localStorage — правила возобновления проверяются тестами.
 */
export interface ExportMemory {
  /** true — команда с таким отпечатком уже выгружена. */
  has(team: ExportTeamPlan): boolean;
  /** Запоминает команду как выгруженную (число файлов — для сообщения экрана). */
  remember(team: ExportTeamPlan, files: number): void;
}

/** Запись о выгруженной команде: отпечаток набора файлов и время выгрузки. */
interface MemoryTeam {
  fingerprint: string;
  files: number;
  savedAt: string;
}

/** Ключ памяти выгрузки в localStorage. */
const MEMORY_KEY = 'eleonora-documents-export';

/** Предел числа запомненных команд: старые записи вытесняются, хранилище не растёт. */
const MEMORY_LIMIT = 1000;

/** Разбирает память из localStorage; битая запись считается пустой (без падения экрана). */
function readMemory(): Record<string, MemoryTeam> {
  try {
    const raw = localStorage.getItem(MEMORY_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, MemoryTeam>) : {};
  } catch {
    return {};
  }
}

/** Записывает память: переполнение localStorage не ломает выгрузку, только возобновление. */
function writeMemory(state: Record<string, MemoryTeam>): void {
  try {
    localStorage.setItem(MEMORY_KEY, JSON.stringify(state));
  } catch {
    // Приватный режим или переполненное хранилище: выгрузка продолжается без памяти.
  }
}

/**
 * Память выгрузки в localStorage, отдельная для каждого мероприятия: выгрузка следующего
 * турнира не должна считать команды прежнего уже скачанными. Ключ записи — id заявки.
 * `resume: false` («скачивать заново») отдаёт пустую память — команды перекачиваются.
 */
export function localStorageMemory(eventName: string, resume: boolean): ExportMemory {
  const prefix = `${eventName}|`;
  const state = resume ? readMemory() : {};
  return {
    has(team) {
      const saved = state[`${prefix}${team.applicationId}`];
      return saved !== undefined && saved.fingerprint === team.fingerprint;
    },
    remember(team, files) {
      state[`${prefix}${team.applicationId}`] = {
        fingerprint: team.fingerprint,
        files,
        savedAt: new Date().toISOString(),
      };
      const keys = Object.keys(state);
      for (const key of keys.slice(0, Math.max(0, keys.length - MEMORY_LIMIT))) delete state[key];
      writeMemory(state);
    },
  };
}

/** Состояние выгрузки для экрана: счётчики и текущая команда. */
export interface ExportProgress {
  teamsDone: number;
  teamsTotal: number;
  filesDone: number;
  filesTotal: number;
  bytesDone: number;
  bytesTotal: number;
  /** Команда, которая качается сейчас (пустая строка — между командами). */
  currentTeam: string;
}

/** Итог выгрузки: сколько выгружено и что не удалось. */
export interface ExportResult {
  teamsTotal: number;
  teamsDone: number;
  filesDone: number;
  bytesDone: number;
  /** Команды, пропущенные как уже выгруженные (память прошлого запуска). */
  skippedTeams: number;
  /** Файлы, которые не удалось скачать после повторов: путь и причина. */
  failures: { path: string; error: string }[];
  /** true — выгрузку остановил администратор. */
  cancelled: boolean;
}

/** Зависимости прогона: в браузере — API, память localStorage и приёмник файлов, в тестах — заглушки. */
export interface ExportIo {
  /** Манифест выгрузки. */
  manifest(): Promise<DocumentsExportManifest>;
  /** Один файл: байты и тип содержимого. */
  file(ref: DocumentRef): Promise<{ bytes: Uint8Array<ArrayBuffer>; contentType: string }>;
  /** Куда складывать файлы. */
  sink: ExportSink;
  /** Память о выгруженных командах. */
  memory: ExportMemory;
  /** Сообщает экрану прогресс. */
  progress(state: ExportProgress): void;
  /** true — администратор нажал «Остановить». */
  isCancelled(): boolean;
  /** Пауза перед повтором сбойного файла (мс); по умолчанию `RETRY_PAUSE_MS`. */
  retryPauseMs?: number;
}

/** Забирает файл с повторами: одиночный сбой сети не должен выпадать из выгрузки. */
async function fileWithRetry(
  io: ExportIo,
  ref: DocumentRef,
): Promise<{ bytes: Uint8Array<ArrayBuffer>; contentType: string }> {
  const pause = io.retryPauseMs ?? RETRY_PAUSE_MS;
  let lastError: unknown;
  for (let attempt = 1; attempt <= FILE_ATTEMPTS; attempt += 1) {
    try {
      return await io.file(ref);
    } catch (err) {
      lastError = err;
      if (attempt < FILE_ATTEMPTS) await delay(pause * attempt);
    }
  }
  throw lastError instanceof Error ? lastError : new Error('Неизвестная ошибка');
}

/** Текст причины сбоя для отчёта: сообщение ошибки, без стека и без содержимого файла. */
function failureText(err: unknown): string {
  return err instanceof Error ? err.message : 'Неизвестная ошибка';
}

/**
 * Выгружает сканы по плану: команда за командой, файлы команды — пулом
 * (`DOWNLOAD_CONCURRENCY` запросов одновременно).
 *
 * Правила, ради которых прогон устроен именно так:
 * - **сбой файла не отменяет выгрузку**: файл попадает в `failures` и в `_ошибки.txt`,
 *   остальные файлы и команды продолжают качаться;
 * - **команда запоминается только целиком**: отмена в середине команды не помечает её
 *   выгруженной, поэтому повторный запуск перекачает её заново (неполный архив команды
 *   на диск не попадает вовсе);
 * - **пропуск уже выгруженных команд** — по отпечатку набора файлов (`ExportMemory`).
 */
export async function runDocumentsExport(io: ExportIo): Promise<ExportResult> {
  const manifest = await io.manifest();
  const plan = buildPlan(manifest);

  const result: ExportResult = {
    teamsTotal: plan.teams.length,
    teamsDone: 0,
    filesDone: 0,
    bytesDone: 0,
    skippedTeams: 0,
    failures: [],
    cancelled: false,
  };

  /** Сообщает экрану состояние счётчиков. */
  function publish(currentTeam: string): void {
    io.progress({
      teamsDone: result.teamsDone,
      teamsTotal: result.teamsTotal,
      filesDone: result.filesDone,
      filesTotal: plan.files,
      bytesDone: result.bytesDone,
      bytesTotal: plan.bytes,
      currentTeam,
    });
  }

  publish('');

  for (const team of plan.teams) {
    if (io.isCancelled()) {
      result.cancelled = true;
      break;
    }
    const manifestTeam = manifest.teams.find((item) => item.applicationId === team.applicationId);
    if (!manifestTeam) continue;
    if (io.memory.has(team)) {
      result.skippedTeams += 1;
      continue;
    }
    publish(team.folder);

    /** Записи архива команды: файлы собираются в памяти по одному. */
    const entries: ZipEntry[] = [];
    const composition = teamCompositionCsv(team, manifestTeam);
    let saved = 0;

    await mapWithLimit(team.files, DOWNLOAD_CONCURRENCY, async (file) => {
      if (io.isCancelled()) return;
      try {
        const answer = await fileWithRetry(io, file.ref);
        entries.push({ path: file.path, bytes: answer.bytes });
        saved += 1;
        result.filesDone += 1;
        result.bytesDone += file.size;
      } catch (err) {
        result.failures.push({ path: file.path, error: failureText(err) });
      }
    });

    // Отмена в середине команды: она не помечается выгруженной, а повторный запуск
    // перекачает её заново — неполный архив команды на диск не попадает.
    if (io.isCancelled()) {
      result.cancelled = true;
      break;
    }

    entries.push({ path: `${team.folder}/_состав.csv`, bytes: csvBytes(composition) });
    await io.sink.write(
      `${team.folder}.zip`,
      new Blob([zipArchive(entries)], { type: 'application/zip' }),
    );

    result.teamsDone += 1;
    io.memory.remember(team, saved);
    publish('');
  }

  // Итоговые файлы: состав всех команд (по нему видно, у кого сканов нет) и список сбоев.
  await writeReportFiles(io, manifest, plan, result);
  return result;
}

/** Пишет итоговые файлы выгрузки: `_состав.csv` по всем командам и `_ошибки.txt` при сбоях. */
async function writeReportFiles(
  io: ExportIo,
  manifest: DocumentsExportManifest,
  plan: ExportPlan,
  result: ExportResult,
): Promise<void> {
  try {
    if (plan.teams.length > 0) {
      await io.sink.write(
        '_состав.csv',
        new Blob([csvBytes(manifestCompositionCsv(manifest, plan))], {
          type: 'text/csv;charset=utf-8',
        }),
      );
    }
    if (result.failures.length > 0) {
      const report = [
        'Не удалось выгрузить файлы (остальные скачаны):',
        ...result.failures.map((failure) => `${failure.path} — ${failure.error}`),
      ].join('\r\n');
      await io.sink.write(
        '_ошибки.txt',
        new Blob([new TextEncoder().encode(report)], { type: 'text/plain;charset=utf-8' }),
      );
    }
  } catch (err) {
    // Сбой записи итоговых файлов не отменяет выгрузку: сканы уже на диске, а причина
    // остаётся в списке сбоев и в сообщении экрана.
    result.failures.push({ path: 'итоговые файлы выгрузки', error: failureText(err) });
  }
}
